import { type MutableRefObject, useCallback, useEffect, useRef } from 'react'
import { useMutation } from '@apollo/client/react'
import * as R from 'ramda'

import { CalculateDifferenceDistributionDocument } from '~/api/graphql/generated/types'
import {
  getExtendedPrice,
  getHighValue,
  getTotal,
} from '~/components/dashboard/invoices/invoiceUtils'
import type { InvoiceLineItem, InvoiceOrEstimate } from '~/types'

export type RunFixedBundleDifferenceDistributionParams = {
  bundleFixedPriceForDistribution: number
  invoiceGroupIndex: number
  itemIndex: number
  itemParent: InvoiceLineItem
  updatedParent: InvoiceLineItem
}

export function useFixedBundleDifferenceDistribution<
  T extends InvoiceOrEstimate,
>({
  invoiceRef,
  isSetBundlePriceEnabled,
  updateInvoice,
}: {
  invoiceRef: MutableRefObject<T>
  isSetBundlePriceEnabled: boolean
  updateInvoice: (invoice: T) => void
}) {
  const distributionAbortRef = useRef<AbortController>()
  const [calculateDifferenceDistribution] = useMutation(
    CalculateDifferenceDistributionDocument,
  )

  useEffect(
    () => () => {
      distributionAbortRef.current?.abort()
    },
    [],
  )

  const runFixedBundleDifferenceDistribution = useCallback(
    ({
      bundleFixedPriceForDistribution,
      invoiceGroupIndex,
      itemIndex,
      itemParent,
      updatedParent,
    }: RunFixedBundleDifferenceDistributionParams) => {
      distributionAbortRef.current?.abort()
      const abortController = new AbortController()
      distributionAbortRef.current = abortController

      calculateDifferenceDistribution({
        variables: {
          values: updatedParent.items!.map((child) =>
            getHighValue(getExtendedPrice(child)),
          ),
          fixedSum: bundleFixedPriceForDistribution,
        },
        context: {
          fetchOptions: {
            signal: abortController.signal,
          },
        },
        onCompleted: (result) => {
          const baseInvoice = invoiceRef.current
          const distributedItems = updatedParent.items!.map((child, index) => {
            const withDiscount: InvoiceLineItem = {
              ...child,
              discountAmount: result.calculateDifferenceDistribution[index],
              discountPerc: null,
            }
            return {
              ...withDiscount,
              subTotal: getTotal(
                withDiscount,
                baseInvoice,
                isSetBundlePriceEnabled,
              ) as number,
            }
          })
          const finalParent: InvoiceLineItem = {
            ...updatedParent,
            items: distributedItems,
            subTotal: getTotal(
              { ...updatedParent, items: distributedItems },
              baseInvoice,
              isSetBundlePriceEnabled,
            ) as number,
          }
          const prevInvoice = invoiceRef.current
          const prevGroups = prevInvoice.groups || []
          const prevGroup = prevGroups[invoiceGroupIndex]
          if (!prevGroup) {
            return
          }
          const newGroupedItems = prevGroup.groupedItems.map(
            (gi: InvoiceLineItem, idx: number) => {
              if (itemParent.id != null) {
                return gi.id === itemParent.id ? finalParent : gi
              }
              return idx === itemIndex ? finalParent : gi
            },
          )
          updateInvoice({
            ...prevInvoice,
            groups: R.update(
              invoiceGroupIndex,
              { ...prevGroup, groupedItems: newGroupedItems },
              prevGroups,
            ),
          })
        },
      })
    },
    [
      calculateDifferenceDistribution,
      invoiceRef,
      isSetBundlePriceEnabled,
      updateInvoice,
    ],
  )

  return { runFixedBundleDifferenceDistribution }
}
